import { Stack, Typography, useTheme } from "@mui/material";
import { type FC, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { usePortfolioQuery } from "web_myplann/Api/Portfolio/usePortfolioQuery";
import { usePropertyQuery } from "web_myplann/Api/Portfolio/usePropertyQuery";
import {
	ClientPortfolioDomainEnum,
	type ClientPropertyArtDetailResponseDTO,
	type ClientPropertyCottageDetailResponseDTO,
	type ClientPropertyFlatDetailResponseDTO,
	type ClientPropertyHouseDetailResponseDTO,
	type ClientPropertyLandDetailResponseDTO,
	ClientPropertyTypeEnum,
	type ClientPropertyVehicleDetailResponseDTO,
	PortfolioTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { BankProduct } from "web_myplann/Portfolio/Components/PortfolioSections/BankProduct";
import { BuildingSavings } from "web_myplann/Portfolio/Components/PortfolioSections/BuildingSavings";
import { Commodities } from "web_myplann/Portfolio/Components/PortfolioSections/Commodities";
import { Cryptocurrency } from "web_myplann/Portfolio/Components/PortfolioSections/Cryptocurrency";
import { Investment } from "web_myplann/Portfolio/Components/PortfolioSections/Investment";
import { LifeInsurance } from "web_myplann/Portfolio/Components/PortfolioSections/LifeInsurance";
import { LoanAll } from "web_myplann/Portfolio/Components/PortfolioSections/LoanAll";
import { PaidAdvisory } from "web_myplann/Portfolio/Components/PortfolioSections/PaidAdvisory";
import { PensionProduct } from "web_myplann/Portfolio/Components/PortfolioSections/PensionProduct";
import { PropertyInsurance } from "web_myplann/Portfolio/Components/PortfolioSections/PropertyInsurance";
import { Services } from "web_myplann/Portfolio/Components/PortfolioSections/Services";
import { Art } from "web_myplann/Portfolio/Components/PropertySections/Art";
import { AutoMoto } from "web_myplann/Portfolio/Components/PropertySections/AutoMoto";
import { Cottage } from "web_myplann/Portfolio/Components/PropertySections/Cottage";
import { Flat } from "web_myplann/Portfolio/Components/PropertySections/Flat";
import { House } from "web_myplann/Portfolio/Components/PropertySections/House";
import { Land } from "web_myplann/Portfolio/Components/PropertySections/Land";
import { hasNoBusinessData } from "web_myplann/Portfolio/Helpers/hasNoBusinessData";
import { usePortfolioTitle } from "web_myplann/Portfolio/Hooks/usePortfolioTitle";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { RowSkeletonLoader } from "web_myplann/Shared/RowSkeletonLoader";
import { DEFAULT_ROUTE } from "web_myplann/Utils/constants";

export const PortfolioDetailPage: FC = () => {
	const { id } = useParams<{ id: string }>();
	const [searchParams] = useSearchParams();
	const { t } = useResource();
	const theme = useTheme();

	const type = searchParams.get("type");

	const { data: portfolio, isLoading: isPortfolioLoading } = usePortfolioQuery(
		Number(id),
		type === PortfolioTypeEnum.Portfolio,
	);

	const { data: property, isLoading: isPropertyLoading } = usePropertyQuery(
		Number(id),
		type === PortfolioTypeEnum.Property,
	);

	const title = usePortfolioTitle(portfolio, property, t);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [id]);

	const currencySign =
		portfolio?.currencySign ?? t(Resources.Common.CurrencySign);

	const showEmpty =
		!isPortfolioLoading &&
		!isPropertyLoading &&
		hasNoBusinessData(portfolio) &&
		hasNoBusinessData(property);

	const isLoading = isPortfolioLoading || isPropertyLoading;

	return (
		<MyPlannLayout
			topBar={<NavigationHeader title={title} defaultRoute={DEFAULT_ROUTE} />}
			disableBottomPadding
		>
			{isLoading ? (
				<DetailSectionLoader />
			) : showEmpty ? (
				<Stack
					alignItems="center"
					justifyContent="center"
					sx={{
						height: "100%",
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
					}}
					spacing={2}
					px={5}
				>
					<RowSkeletonLoader />
					<RowSkeletonLoader />
					<RowSkeletonLoader />
					<Stack textAlign="center" gap={0.4}>
						<Typography variant="h3" color={theme.palette.grey[700]}>
							{t(Resources.PortfolioDetail.SharedLabels.empty)}
						</Typography>
						<Typography variant="body1" color={theme.palette.grey[500]}>
							{t(Resources.PortfolioDetail.SharedLabels.emptySubtitle)}
						</Typography>
					</Stack>
				</Stack>
			) : (
				<Stack pt={2}>
					{property?.type === ClientPropertyTypeEnum.AutoMoto && (
						<AutoMoto
							data={property as ClientPropertyVehicleDetailResponseDTO}
							currencySign={currencySign}
							documentsId={Number(id)}
						/>
					)}
					{property?.type === ClientPropertyTypeEnum.Art && (
						<Art
							data={property as ClientPropertyArtDetailResponseDTO}
							currencySign={currencySign}
							documentsId={Number(id)}
						/>
					)}
					{property?.type === ClientPropertyTypeEnum.Cottage && (
						<Cottage
							data={property as ClientPropertyCottageDetailResponseDTO}
							currencySign={currencySign}
							documentsId={Number(id)}
						/>
					)}
					{property?.type === ClientPropertyTypeEnum.Flat && (
						<Flat
							data={property as ClientPropertyFlatDetailResponseDTO}
							currencySign={currencySign}
							documentsId={Number(id)}
						/>
					)}
					{property?.type === ClientPropertyTypeEnum.House && (
						<House
							data={property as ClientPropertyHouseDetailResponseDTO}
							currencySign={currencySign}
							documentsId={Number(id)}
						/>
					)}
					{property?.type === ClientPropertyTypeEnum.Land && (
						<Land
							data={property as ClientPropertyLandDetailResponseDTO}
							currencySign={currencySign}
							documentsId={Number(id)}
						/>
					)}

					{portfolio?.domain === ClientPortfolioDomainEnum.LifeInsurance && (
						<LifeInsurance baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.Investments && (
						<Investment baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain ===
						ClientPortfolioDomainEnum.PropertyInsurance && (
						<PropertyInsurance
							baseData={portfolio}
							currencySign={currencySign}
						/>
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.PensionProducts && (
						<PensionProduct baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.BuildingSavings && (
						<BuildingSavings baseData={portfolio} currencySign={currencySign} />
					)}
					{(portfolio?.domain === ClientPortfolioDomainEnum.Loans ||
						portfolio?.domain === ClientPortfolioDomainEnum.Leasing ||
						portfolio?.domain === ClientPortfolioDomainEnum.BusinessLoans) && (
						<LoanAll baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.Commodities && (
						<Commodities baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.BankingProducts && (
						<BankProduct baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.Cryptocurrencies && (
						<Cryptocurrency baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.Services && (
						<Services baseData={portfolio} currencySign={currencySign} />
					)}
					{portfolio?.domain === ClientPortfolioDomainEnum.PaidAdvisory && (
						<PaidAdvisory baseData={portfolio} currencySign={currencySign} />
					)}
				</Stack>
			)}
		</MyPlannLayout>
	);
};
